/**
 * GhostScrubber
 *
 * Controls the Ghost Race animation:
 *   - Play / Pause
 *   - Speed selector: 30× / 60× / 300× / 600×
 *   - Elapsed time display
 *   - Draggable progress slider
 *   - Jump to: Start, Lead changes, Checkpoints, Climbs
 *
 * Drives the animation via requestAnimationFrame.
 * Emits onTimeChange(T) where T is elapsed race seconds.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../theme';

const SPEEDS = [30, 60, 300, 600];

function formatHms(seconds) {
  if (seconds == null || isNaN(seconds)) return '0:00:00';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
}

// Detect lead changes from resampled array
function findLeadChangeTimes(resampled) {
  const times = [];
  for (let i = 1; i < resampled.length; i++) {
    const prev = resampled[i - 1];
    const curr = resampled[i];
    if (Math.sign(prev.time_delta_s) !== Math.sign(curr.time_delta_s) && prev.time_delta_s !== 0 && curr.time_delta_s !== 0) {
      times.push((prev.elapsed_a_s + curr.elapsed_a_s) / 2);
    }
  }
  return times;
}

export default function GhostScrubber({ resampled = [], scrubberTime, onTimeChange, checkpoints = null }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(60);
  const rafRef = useRef(null);
  const lastFrameRef = useRef(null);

  const maxTime = useMemo(() => {
    if (!resampled.length) return 0;
    const last = resampled[resampled.length - 1];
    return Math.max(last.elapsed_a_s ?? 0, last.elapsed_b_s ?? 0);
  }, [resampled]);

  const leadChangeTimes = useMemo(() => findLeadChangeTimes(resampled), [resampled]);

  // Animation loop
  useEffect(() => {
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastFrameRef.current = null;
      return;
    }

    const tick = (now) => {
      if (lastFrameRef.current !== null) {
        const dt = (now - lastFrameRef.current) / 1000; // real seconds since last frame
        onTimeChange((prev) => {
          const next = prev + dt * speed;
          if (next >= maxTime) {
            setPlaying(false);
            return maxTime;
          }
          return next;
        });
      }
      lastFrameRef.current = now;
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [playing, speed, maxTime, onTimeChange]);

  const handleSliderChange = (_, val) => {
    onTimeChange(val);
  };

  const handleJump = (time) => {
    onTimeChange(time);
    setPlaying(false);
  };

  const handleRestart = () => {
    onTimeChange(0);
    setPlaying(true);
  };

  // Build Jump-to options
  const jumpOptions = useMemo(() => {
    const opts = [{ label: t('start', 'Start'), time: 0 }];
    leadChangeTimes.forEach((lc, i) => opts.push({ label: t('leadChangeN', 'Lead change {{n}}', { n: i + 1 }), time: lc }));
    checkpoints?.forEach((cp, i) => {
      if (cp.arrived_a_s != null) opts.push({ label: cp.name || `CP ${i + 1}`, time: cp.arrived_a_s });
    });
    return opts;
  }, [leadChangeTimes, checkpoints, t]);

  return (
    <Box
      sx={{
        backgroundColor: colors.grey[800],
        borderRadius: 2,
        p: 2,
      }}
    >
      {/* Top row: play + speed + time display */}
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        {/* Play / Pause / Restart */}
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton onClick={handleRestart} size="small" sx={{ color: colors.grey[400] }}>
            <ReplayIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => setPlaying((p) => !p)}
            size="small"
            sx={{ color: colors.blueAccent[300], backgroundColor: colors.grey[700], '&:hover': { backgroundColor: colors.grey[600] } }}
          >
            {playing ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Box>

        {/* Speed */}
        <ToggleButtonGroup
          value={speed}
          exclusive
          onChange={(_, v) => { if (v != null) setSpeed(v); }}
          size="small"
        >
          {SPEEDS.map((s) => (
            <ToggleButton
              key={s}
              value={s}
              sx={{
                color: colors.grey[400],
                borderColor: colors.grey[600],
                fontSize: '0.7rem',
                px: 1,
                '&.Mui-selected': { color: colors.blueAccent[300], backgroundColor: colors.grey[700] },
              }}
            >
              {s}×
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Elapsed time */}
        <Typography
          variant="h6"
          fontWeight="bold"
          color={colors.grey[100]}
          sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 80 }}
        >
          {formatHms(scrubberTime)}
        </Typography>

        {/* Jump to */}
        {jumpOptions.length > 1 && (
          <Select
            value=""
            displayEmpty
            size="small"
            onChange={(e) => handleJump(e.target.value)}
            sx={{
              color: colors.grey[300],
              fontSize: '0.75rem',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
              minWidth: 130,
            }}
            renderValue={() => t('jumpTo', 'Jump to…')}
          >
            {jumpOptions.map((opt, i) => (
              <MenuItem key={i} value={opt.time} sx={{ fontSize: '0.8rem' }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </Box>

      {/* Progress slider */}
      <Box mt={1} px={0.5}>
        <Slider
          value={scrubberTime}
          min={0}
          max={maxTime}
          step={1}
          onChange={handleSliderChange}
          sx={{
            color: colors.blueAccent[400],
            '& .MuiSlider-thumb': { width: 14, height: 14 },
          }}
        />
      </Box>
    </Box>
  );
}
